import {
  addDays,
  authoredWeeks,
  latticeWeekStart,
  resolveFlexibleOccurrence,
} from "./flexible-program";

/**
 * The rolling window behind the Flexible Program preview: `cardCount`
 * consecutive lattice weeks, each resolved on its own.
 *
 * Kept out of flexible-program.js on purpose - that file is mirrored
 * function-for-function into msb-api and msb-mobile, and this is GUI-only.
 *
 * Each card is resolved independently rather than read off
 * listFlexibleOccurrences, because that walk stops at the first non-"active"
 * week and would simply omit the "ended" cards this window has to show.
 *
 * `weekOffset` is relative to the week containing today (clamped to the
 * program's first week by latticeWeekStart), so 0 always opens on "now".
 */
export function buildPreviewWindow(
  cycle,
  { todayYyyymmdd, weekOffset = 0, cardCount = 6 }
) {
  const weeks = authoredWeeks(cycle);
  if (!weeks.length) {
    return { cards: [], disableBack: true, disableForward: true };
  }

  const anchor = Number(weeks[0].startDate);
  const home = latticeWeekStart(cycle, todayYyyymmdd);
  // Never earlier than the program's own first week, however far back the
  // URL asks for.
  const windowStart = Math.max(anchor, addDays(home, weekOffset * 7));

  const cards = Array.from({ length: cardCount }, (_, index) => {
    const cardStart = addDays(windowStart, index * 7);
    return {
      cardStart,
      cardEnd: addDays(cardStart, 6),
      resolved: resolveFlexibleOccurrence(cycle, cardStart),
    };
  });

  const lastCard = cards[cards.length - 1];
  // "ended" only ever means the live week stopped repeating; it only stays
  // that way if nothing is authored to start after it.
  const authoredAfterWindow = weeks.some(
    (week) => Number(week.startDate) > lastCard.cardStart
  );

  return {
    cards,
    windowStart,
    disableBack: windowStart <= anchor,
    disableForward:
      lastCard.resolved?.status === "ended" && !authoredAfterWindow,
  };
}
